import { QueryClient, QueryClientProvider } from "react-query";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import "./styles/toastify-overrides.css";
import AdminRoute from "./components/AdminRoute";
import AppMessagesRoute from "./components/AppMessagesRoute";
import Layout from "./components/Layout";
import PrivateRoute from "./components/PrivateRoute";
import ResellerRoute from "./components/ResellerRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { GradientProvider } from "./contexts/GradientContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { SearchProvider } from "./contexts/SearchContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import Achievements from "./pages/Achievements";
import AppMessages from "./pages/AppMessages";
import CustomerDetails from "./pages/CustomerDetails";
import CustomerDevices from "./pages/CustomerDevices";
import CustomerPlaylists from "./pages/CustomerPlaylists";
import Customers from "./pages/Customers";
import Dashboard from "./pages/Dashboard";
import FAQ from "./pages/FAQ";
import FAQManager from "./pages/FAQManager";
import ForgotPassword from "./pages/ForgotPassword";
import InvoiceDetail from "./pages/InvoiceDetail";
import Invoices from "./pages/Invoices";
import Login from "./pages/Login";
import ManageSports from "./pages/ManageSports";
import MovieCategoryView from "./pages/MovieCategoryView";
import NotFound from "./pages/NotFound";
import Notifications from "./pages/Notifications";
import PlaylistManager from "./pages/PlaylistManager";
import PredefinedRepliesManager from "./pages/PredefinedRepliesManager";
import Profile from "./pages/Profile";
import ProfileSelector from "./pages/ProfileSelector";
import Register from "./pages/Register";
import Reports from "./pages/Reports";
import ResellerDashboardPreview from "./pages/ResellerDashboardPreview";
import ResellerPlans from "./pages/ResellerPlans";
import Resellers from "./pages/Resellers";
import ResellerSettings from "./pages/ResellerSettings";
import ResellerTiers from "./pages/ResellerTiers";
import ResetPassword from "./pages/ResetPassword";
import SeriesCategoryView from "./pages/SeriesCategoryView";
import TicketDetail from "./pages/TicketDetail";
import Tickets from "./pages/Tickets";
import UserDetails from "./pages/UserDetails";
import Users from "./pages/Users";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppContent() {
  const { isDarkMode } = useTheme();

  return (
    <Router>
      <SearchProvider>
        <NotificationProvider>
          <ToastContainer
            closeOnClick
            draggable
            pauseOnFocusLoss
            pauseOnHover
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop={false}
            position="top-right"
            rtl={false}
            style={{
              zIndex: 999999,
            }}
            theme={isDarkMode ? "dark" : "light"}
          />
          <Routes>
            <Route element={<Login />} path="/login" />
            <Route element={<Register />} path="/register" />
            <Route element={<ResetPassword />} path="/reset-password" />
            <Route element={<ForgotPassword />} path="/forgot-password" />
            <Route
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
              path="/"
            >
              <Route index element={<Navigate replace to="/dashboard" />} />
              <Route element={<Dashboard />} path="dashboard" />
              <Route element={<Tickets />} path="tickets" />
              <Route element={<TicketDetail />} path="tickets/:id" />
              <Route element={<Invoices />} path="invoices" />
              <Route element={<InvoiceDetail />} path="invoices/:id" />
              <Route
                element={
                  <AdminRoute>
                    <Customers />
                  </AdminRoute>
                }
                path="customers"
              />
              <Route
                element={
                  <AdminRoute>
                    <CustomerDetails />
                  </AdminRoute>
                }
                path="customers/:id"
              />
              <Route
                element={
                  <AdminRoute>
                    <Users />
                  </AdminRoute>
                }
                path="users"
              />
              <Route
                element={
                  <AdminRoute>
                    <UserDetails />
                  </AdminRoute>
                }
                path="users/:id"
              />
              <Route element={<Reports />} path="reports" />
              <Route
                element={
                  <AdminRoute>
                    <Resellers />
                  </AdminRoute>
                }
                path="resellers"
              />
              <Route element={<ResellerTiers />} path="reseller-tiers" />
              <Route
                element={
                  <ResellerRoute>
                    <ResellerPlans />
                  </ResellerRoute>
                }
                path="reseller-plans"
              />
              <Route
                element={
                  <ResellerRoute>
                    <ResellerSettings />
                  </ResellerRoute>
                }
                path="reseller-settings"
              />
              <Route
                element={<ResellerDashboardPreview />}
                path="dashboard-preview"
              />
              <Route element={<Achievements />} path="achievements" />
              <Route
                element={<ProfileSelector />}
                path="playlist-manager/:playlistId"
              />
              <Route
                element={<PlaylistManager />}
                path="playlist-manager/:playlistId/manage"
              />
              <Route
                element={<MovieCategoryView />}
                path="playlist-manager/:playlistId/manage/movies/:categoryId/:categoryName"
              />
              <Route
                element={<SeriesCategoryView />}
                path="playlist-manager/:playlistId/manage/series/:categoryId/:categoryName"
              />
              <Route element={<CustomerDevices />} path="devices" />
              <Route element={<CustomerPlaylists />} path="playlists" />
              <Route element={<Profile />} path="profile" />
              <Route element={<Notifications />} path="notifications" />
              <Route element={<FAQ />} path="faqs" />
              <Route element={<FAQManager />} path="faq-manager" />
              <Route
                element={
                  <AdminRoute>
                    <PredefinedRepliesManager />
                  </AdminRoute>
                }
                path="predefined-replies"
              />
              <Route
                element={
                  <AdminRoute>
                    <ManageSports />
                  </AdminRoute>
                }
                path="manage-sports"
              />
              <Route
                element={
                  <AppMessagesRoute>
                    <AppMessages />
                  </AppMessagesRoute>
                }
                path="app-messages"
              />
              <Route element={<NotFound />} path="*" />
            </Route>
            <Route element={<NotFound />} path="*" />
          </Routes>
        </NotificationProvider>
      </SearchProvider>
    </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GradientProvider>
        <ThemeProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ThemeProvider>
      </GradientProvider>
    </QueryClientProvider>
  );
}

export default App;
